import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';

import { useAuth } from '../../context/AuthContext';
import { notificationsApi } from '../../services/api';
import { dueTone, formatDueDate } from '../ui/DueDateBadge';
import { formatIST } from '../../utils/dateUtils';

// Deadline bell for reviewers and approvers. Shows the same items the reminder
// emails cover, so the badge count and the inbox always agree.
//
// The bell belongs to exactly one queue, decided by the hat you are wearing:
// the section of the app you are in, or your active role on pages that belong
// to no section. Staff accounts routinely hold reviewer, approver and admin at
// once, and the queues are unrelated - review deadlines are not an approver's
// business, and neither of them belongs in the admin or creator sections. So
// anywhere outside those two roles there is no bell at all.

const REFRESH_MS = 2 * 60 * 1000;
const SEEN_KEY = 'unify.deadlines.seen';

const ROLE_LABELS = {
  reviewer: 'Review deadlines',
  approver: 'Approval deadlines',
};

// Sections that own a deadline queue. Matched on the path itself so a route
// like /reviewers-guide could never be mistaken for the reviewer section.
const ROLE_SECTIONS = [
  ['/reviewer', 'reviewer'],
  ['/approver', 'approver'],
];

const sectionRole = (pathname) => {
  const hit = ROLE_SECTIONS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  return hit ? hit[1] : null;
};

// Badge identity for one item. Opening the panel marks the current key as seen,
// so the badge comes back whenever the key changes:
//
// - Once the item is inside the reminder window the key carries days-left, so
//   the badge returns daily, in step with the reminder email.
// - Before that it carries the moment the item landed in your queue, so a new
//   assignment or a reassignment is flagged the moment it happens - a deadline
//   a week out used to show nothing at all - and then stays quiet until the
//   due date comes close.
// Keys carry the queue as well, so dismissing your approval badges never
// touches what is waiting for you as a reviewer.
const seenKey = (item, lead) =>
  item.days_left <= lead
    ? `${item.role}:${item.id}:due:${item.days_left}`
    : `${item.role}:${item.id}:new:${item.assigned_at || item.due_date}`;

const loadSeen = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
  } catch {
    return new Set();
  }
};

const saveSeen = (keys) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...keys]));
  } catch {
    // Private browsing / storage full - the badge just stops persisting.
  }
};

// When the last reminder email for this item actually went out. It is read back
// from the row the sender writes, so it reports delivery rather than repeating
// the schedule - if this line stops moving, the reminders have stopped.
const ReminderStamp = ({ item, lead }) => {
  if (item.last_reminder_status === 'failed') {
    return (
      <p className="mt-1 text-[11px] text-red-600">
        Reminder failed - {formatIST(item.last_reminded_at)}
      </p>
    );
  }
  return (
    <p className="mt-1 text-[11px] text-gray-400">
      {item.last_reminded_at
        ? `Reminder sent - ${formatIST(item.last_reminded_at)}`
        : ''}
    </p>
  );
};

// 9 -> "9:00 AM". The hour itself comes from the backend so the panel cannot
// drift out of step with the job.
const sendTimeLabel = (hour) => `${((hour + 11) % 12) + 1}:00 ${hour < 12 ? 'AM' : 'PM'}`;

const itemLink = (item) =>
  item.role === 'reviewer'
    ? `/reviewer/courses/${item.course_id}${item.batch_id ? `?batch_id=${item.batch_id}` : ''}`
    : `/approver/courses/${item.course_id}${item.batch_id ? `?batch_id=${item.batch_id}` : ''}`;

const NotificationBell = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [data, setData] = useState({ items: [], due_soon: 0, overdue: 0 });
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(loadSeen);
  const wrapRef = useRef(null);

  // The section wins over the stored active role, since that is the hat the
  // user is visibly wearing right now: an admin who opens the reviewer section
  // is reviewing. Anywhere else - the admin dashboard, the creator pages, the
  // public site - an admin is not a reviewer, so `null` means no bell.
  const activeRole = user?.active_role || user?.role;
  const scope =
    sectionRole(pathname) ||
    (['reviewer', 'approver'].includes(activeRole) ? activeRole : null);

  const fetchDeadlines = useCallback(async () => {
    // Never ask without a queue: the endpoint would have to guess, and guessing
    // is what mixes the two queues together.
    if (!scope) return;
    try {
      const response = await notificationsApi.getDeadlines(scope);
      if (response.data.success) setData(response.data.data);
    } catch (error) {
      // A failing bell must never disrupt the page.
      console.error('Error fetching deadlines:', error);
    }
  }, [scope]);

  useEffect(() => {
    // `fetchDeadlines` only changes identity when the queue does, so this
    // clears the previous role's items the moment you switch hats - an approver
    // must never see the review list, not even for the length of a request.
    setData({ items: [], due_soon: 0, overdue: 0 });
    fetchDeadlines();
    const id = setInterval(fetchDeadlines, REFRESH_MS);

    // Coming back to the tab is the moment a reviewer is most likely to be
    // waiting on something they were just assigned, so refresh then too rather
    // than sitting on stale counts until the next tick.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchDeadlines();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchDeadlines]);

  // Switching section changes what the bell is about, so drop the open panel.
  useEffect(() => setOpen(false), [scope]);

  // Close when clicking anywhere else
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const items = data.items || [];
  const lead = data.lead_days ?? 5;

  // The badge counts everything waiting on you that you have not looked at yet.
  // Work already done (every question reviewed) never raises the badge.
  const unseen = items.filter((i) => !i.work_done && !seen.has(seenKey(i, lead)));
  const count = unseen.length;
  const urgent = unseen.some((i) => i.days_left < 0);

  const openPanel = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        // Mark everything currently badged as seen, and drop keys for items that
        // are gone or have moved on so the store cannot grow forever. Only this
        // queue's keys are up for pruning - the other one is not loaded here,
        // so every one of its keys would look dead.
        const live = new Set(items.map((i) => seenKey(i, lead)));
        const next = new Set(
          [...seen].filter((k) => !k.startsWith(`${scope}:`) || live.has(k))
        );
        unseen.forEach((i) => next.add(seenKey(i, lead)));
        setSeen(next);
        saveSeen(next);
      }
      return !wasOpen;
    });
  };

  // No queue, no bell. Kept below the hooks so the rules of hooks still hold.
  if (!scope) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={openPanel}
        className="relative p-2 rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors"
        title={count ? `${count} deadline${count === 1 ? '' : 's'} need attention` : 'No new deadlines'}
      >
        <BellIcon className="h-6 w-6" />
        {count > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold
              flex items-center justify-center text-white ${urgent ? 'bg-red-500' : 'bg-amber-500'}`}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">{ROLE_LABELS[scope]}</p>
            <p className="text-xs text-gray-500">
              {items.length === 0
                ? 'Nothing is assigned to you right now'
                : `${items.length} item${items.length === 1 ? '' : 's'} waiting on you`}
            </p>
          </div>

          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">You are all caught up.</p>
            ) : (
              items.map((item) => {
                const tone = item.work_done
                  ? { label: 'All reviewed', className: 'bg-green-50 text-green-700 border-green-200' }
                  : dueTone(item.days_left);
                return (
                  <Link
                    key={item.id}
                    to={itemLink(item)}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900 truncate" title={item.title}>
                      {item.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {tone && (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${tone.className}`}>
                          {tone.label}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {item.item_type === 'batch' ? 'Question batch' : 'Course'} - {formatDueDate(item.due_date)}
                      </span>
                    </div>
                    <ReminderStamp item={item} lead={lead} />
                  </Link>
                );
              })
            )}
          </div>

          {/* <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
            <p className="text-[11px] text-gray-500">
              {data.reminders_enabled === false
                ? 'Reminder emails are switched off in system settings.'
                : `Sent daily at ${sendTimeLabel(data.send_hour_ist ?? 9)} IST, from ${lead} days before the due date.`}
            </p>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
